import React from 'react';
import TypographyMUI from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import Tooltip, { useTooltip } from '../Tooltip'; // Correct import of Tooltip and useTooltip
import type { TypographyProps } from './types';

const getColor = (
  error: TypographyProps['error'],
  disabled: TypographyProps['disabled'],
  color: TypographyProps['color'],
): string => {
  if (error) return 'error.main';
  if (disabled) return 'gray';
  if (typeof color === 'string') return color;
  return 'inherit';
};

const Typography = React.forwardRef<HTMLDivElement, TypographyProps>(({
  variant,
  paddingLeft = 0,
  paddingRight = 0,
  centerVertically = false,
  bold = false,
  italic = false,
  striked = false,
  showSkeleton,
  expectedTextSize,
  breakWord = true,
  color = 'inherit',
  error = false,
  disabled = false,
  enableClickToCopy = false,
  tooltip,
  sx = {},
  sxExtra = {},
  ...rest
}, ref) => {
  const {
    handleCopy,
    handleMouseEnter,
    handleMouseLeave,
    tooltipText,
    tooltipOpen,
  } = useTooltip({ enableClickToCopy, tooltip });

  if (showSkeleton) {
    return (
      <Skeleton width={expectedTextSize || '100%'}>
        <TypographyMUI
          {...rest}
          ref={ref}
          variant={variant}
          sx={{ ...sx }}
        >
          {' '}
        </TypographyMUI>
      </Skeleton>
    );
  }

  const typographyColor = getColor(error, disabled, color);

  if (!tooltipText && !enableClickToCopy) {
    <TypographyMUI
      {...rest}
      ref={ref}
      onClick={() => {
        if (typeof rest.children === 'string') {
          handleCopy(rest.children as string);
        }
      }}
      sx={{
        ...sx,
        whiteSpace: breakWord ? 'pre-wrap' : 'nowrap',
        wordBreak: breakWord ? 'break-word' : 'keep-all',
        paddingLeft: `${paddingLeft}px`,
        paddingRight: `${paddingRight}px`,
        alignSelf: centerVertically ? 'center' : undefined,
        fontWeight: bold ? 'bold' : 'normal',
        fontStyle: italic ? 'italic' : 'normal',
        textDecoration: striked ? 'line-through' : 'none',
        color: typographyColor,
        '&:hover': {
          cursor: enableClickToCopy ? 'copy' : 'unset',
          backgroundColor: enableClickToCopy ? '#F5F5F5 !important' : 'unset',
          // paddingTop: enableClickToCopy ? '2px' : 'unset',
          // paddingBottom: enableClickToCopy ? '2px' : 'unset',
        },
        ...sxExtra,
      }}
      variant={variant}
    >
      {rest.children}
    </TypographyMUI>
  };
  
  return (
    <Tooltip
      /* title={tooltipText} */
      open={tooltipOpen}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <TypographyMUI
        {...rest}
        ref={ref}
        onClick={() => {
          if (typeof rest.children === 'string') {
            handleCopy(rest.children as string);
          }
        }}
        sx={{
          ...sx,
          whiteSpace: breakWord ? 'pre-wrap' : 'nowrap',
          wordBreak: breakWord ? 'break-word' : 'keep-all',
          paddingLeft: `${paddingLeft}px`,
          paddingRight: `${paddingRight}px`,
          alignSelf: centerVertically ? 'center' : undefined,
          fontWeight: bold ? 'bold' : 'normal',
          fontStyle: italic ? 'italic' : 'normal',
          textDecoration: striked ? 'line-through' : 'none',
          color: typographyColor,
          '&:hover': {
            cursor: enableClickToCopy ? 'copy' : 'unset',
            backgroundColor: enableClickToCopy ? '#F5F5F5 !important' : 'unset',
            // paddingTop: enableClickToCopy ? '2px' : 'unset',
            // paddingBottom: enableClickToCopy ? '2px' : 'unset',
          },
          ...sxExtra,
        }}
        variant={variant}
      >
        {rest.children}
      </TypographyMUI>
    </Tooltip>
  );
});

export default Typography;
